import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import useContent from 'store/content'
import langEN from '../../../public/locales/en.json'
import langFR from '../../../public/locales/fr.json'
import { useLanguage } from '../../hooks/useLanguage'
import { getCookieConfig } from './config'
import {
  getInitialServicesState,
  isCategoryChecked,
  servicesFromConsent,
  setAllServices,
  toggleCategory
} from './gdpr.utils'
import { StyledButtons, StyledCheckboxes, StyledContent, StyledForm, StyledLang, StyledLink } from './styles'
import { useCookiePreferences } from './useCookiePreferences'

const CategoryAccordion = ({ lang, cat, isExpanded, onToggleExpand, masterValue, onMasterToggle, servicesState, onServiceToggle }) => {
  const hasServices = cat.services.length > 0

  return (
    <div className="category-item">
      <div className="category-header">
        <span
          className="category-trigger"
          onClick={hasServices ? onToggleExpand : undefined}
          style={{ cursor: hasServices ? 'pointer' : 'default' }}
        >
          {hasServices && (isExpanded ? '▼ ' : '► ')}{cat.title}
        </span>
        <div className="checkbox-container">
          <Checkbox label="" onChange={onMasterToggle} value={masterValue} disabled={cat.disabled} required={false} />
        </div>
      </div>

      {(!hasServices || isExpanded) && (
        <div className="service-container">
          <p className="category-description">{cat.description}</p>
          {hasServices && cat.services.map((service) => (
            <div className="service-header" key={service.id}>
              <span className="service-trigger">{service.label?.[lang]}</span>
              <Checkbox
                label=""
                onChange={() => onServiceToggle(service.id)}
                value={!!servicesState[service.id]}
                required={false}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const GDPRPopup = () => {
  const [open, setOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const { language: lang, toggleLanguage } = useLanguage()
  const [services, setServices] = useState({})
  const [expanded, setExpanded] = useState({})

  const content = useContent()
  const GDPRConfig = content.get.GDPRConfig()
  const appId = content.get.appId()
  const legalName = content.get.legalContactName()

  const langFile = lang === 'en' ? langEN.gdpr : langFR.gdpr
  const legalSlug = lang === 'en' ? 'privacy-policy' : 'vie-privee'
  const tosSlug = langFile.tos
  const msjPolicy = `https://mysmartjourney.com/legal/${legalSlug}`
  const msjTos = `https://mysmartjourney.com/legal/${tosSlug}`

  const policySuffix = lang === 'en' ? 'En' : 'Fr'
  const cookiePolicy = GDPRConfig?.[`cookiePolicy${policySuffix}`]
  const tosPolicy = GDPRConfig?.[`tosPolicy${policySuffix}`]
  const piiPolicy = GDPRConfig?.[`piiPolicy${policySuffix}`]

  const categoryConfig = getCookieConfig(langFile)
  const {cookiePreferences, updateCookiePreferences} = useCookiePreferences();

  const askConsent = GDPRConfig?.askConsent !== false

  useEffect(() => {
    if (!askConsent) return
    const handleUpdate = () => setIsVisible(true)
    window.addEventListener('update_cookie_consent', handleUpdate)
    return () => window.removeEventListener('update_cookie_consent', handleUpdate)
  }, [askConsent])

  useEffect(() => {
    if (!GDPRConfig) return

    if (!askConsent) {
      const allServices = setAllServices(categoryConfig, true)
      const alreadyGranted = cookiePreferences && Object.keys(allServices).every(id => cookiePreferences[id])

      if (!alreadyGranted) updateCookiePreferences(allServices)
      setIsVisible(false)
      return
    }

    setIsVisible(!cookiePreferences)

    setServices(
      cookiePreferences
        ? servicesFromConsent(categoryConfig, cookiePreferences)
        : getInitialServicesState(categoryConfig)
    )
  }, [cookiePreferences, GDPRConfig, askConsent, appId])

  const handleServiceToggle = (id) => setServices(service => ({ ...service, [id]: !service[id] }))
  const toggleExpand = (id) => setExpanded(service => ({ ...service, [id]: !service[id] }))

  const handleCategoryToggle = (cat) => {
    if (cat.disabled || cat.services.length === 0) return
    setServices(service => toggleCategory(cat, service))
  }

  const handleOnSubmit = (e, actionType) => {
    e?.preventDefault()
    const fs = actionType === 'all'
      ? setAllServices(categoryConfig, true)
      : actionType === 'none'
        ? setAllServices(categoryConfig, false)
        : services

    updateCookiePreferences(fs)
    setIsVisible(false)
  }

  if (!GDPRConfig || !askConsent || !isVisible) return null

  return (
    <StyledForm $isVisible={isVisible}>
      <StyledContent>
        <StyledLang>
          <button type="button" onClick={toggleLanguage}>
            {lang === 'en' ? 'Français' : 'English'}
          </button>
        </StyledLang>

        <h1>{langFile.cookies}</h1>
        <p>{langFile.text}</p>

        {GDPRConfig.useMsjPolicy &&
          <>
            <StyledLink href={msjPolicy} target="_blank" rel="noreferrer">
              MySmartJourney - {langFile.policy}
            </StyledLink>
            <StyledLink href={msjTos} target="_blank" rel="noreferrer">
              MySmartJourney - {langFile.tos}
            </StyledLink>
          </>
        }

        {cookiePolicy && (
          <StyledLink href={cookiePolicy} target="_blank" rel="noreferrer">
            {legalName} - {langFile.policy}
          </StyledLink>
        )}
        {tosPolicy && (
          <StyledLink href={tosPolicy} target="_blank" rel="noreferrer">
            {legalName} - {langFile.tos}
          </StyledLink>
        )}
        {piiPolicy && (
          <StyledLink href={piiPolicy} target="_blank" rel="noreferrer">
            {legalName} - {langFile.pii}
          </StyledLink>
        )}

        {open && (
          <StyledCheckboxes>
            {categoryConfig.map((cat) => (
              <CategoryAccordion
                lang={lang === 'en' ? 'en' : 'fr'}
                key={cat.id}
                cat={cat}
                isExpanded={!!expanded[cat.id]}
                onToggleExpand={() => toggleExpand(cat.id)}
                masterValue={isCategoryChecked(cat, services)}
                onMasterToggle={() => handleCategoryToggle(cat)}
                servicesState={services}
                onServiceToggle={handleServiceToggle}
              />
            ))}
          </StyledCheckboxes>
        )}

        <StyledButtons>
          {open ? (
            <div className="button-group">
              <Button outline onClick={(e) => handleOnSubmit(e, 'none')}>
                {langFile.refuseAll}
              </Button>
              <Button onClick={(e) => handleOnSubmit(e, 'custom')}>{langFile.confirm}</Button>
            </div>
          ) : (
            <>
              <Button outline onClick={() => setOpen(true)}>
                {langFile.configure}
              </Button>
              <div className="button-group">
                <Button outline onClick={(e) => handleOnSubmit(e, 'none')}>
                  {langFile.refuseAll}
                </Button>
                <Button onClick={(e) => handleOnSubmit(e, 'all')}>{langFile.accept}</Button>
              </div>
            </>
          )}
        </StyledButtons>
      </StyledContent>
    </StyledForm>
  )
}

export default withTranslation()(GDPRPopup)